import { formatCount, localTimeWithZone, utcDayLabel } from '@/utils/sendingDomainWarmup'
import type { BroadcastPlanDay } from './sendingDomainPlan'

/**
 * The broadcast's days under its progress bar (ORB-953): what each past day sent and what each coming
 * day is expected to send, each under its own share of the bar. A day too narrow for its words takes the
 * room it needs, and a long plan scrolls rather than squeezing.
 */
export function BroadcastDayPlan({ days, recipients }: { days: BroadcastPlanDay[]; recipients: number }) {
  if (days.length === 0) return null

  const covered = days.reduce((sum, day) => sum + day.sent + day.expected, 0)
  // Whatever never sends, or misses the event, keeps its share at the end so the days stay under the bar.
  const rest = Math.max(0, recipients - covered)
  const columns = days.map(day => `minmax(min-content, ${day.sent + day.expected}fr)`)
  if (rest > 0) columns.push(`minmax(0, ${rest}fr)`)

  return (
    <div className="overflow-x-auto">
      <ol aria-label="Day by day" className="grid" style={{ gridTemplateColumns: columns.join(' ') }}>
        {days.map(day => (
          <li key={day.dayStartUtc} className="flex flex-col gap-0.5 px-2 first:pl-0 last:pr-0">
            <span className="whitespace-nowrap text-xs font-medium text-slate-700 dark:text-slate-200">
              {utcDayLabel(day.dayStartUtc)}
            </span>
            {day.isToday || day.sent > 0 ? <SentDay day={day} /> : <ExpectedDay day={day} />}
          </li>
        ))}
      </ol>
    </div>
  )
}

function SentDay({ day }: { day: BroadcastPlanDay }) {
  return (
    <>
      <span className="whitespace-nowrap text-[13px] text-slate-600 dark:text-slate-300">
        <span className="font-semibold tabular-nums text-slate-900 dark:text-white">{formatCount(day.sent)}</span> sent
      </span>
      {day.expected > 0 && (
        <span className="whitespace-nowrap text-xs text-slate-500 dark:text-slate-400">
          {formatCount(day.expected)} more expected
        </span>
      )}
      {day.isToday && (
        <span className="mt-0.5">
          <span className="inline-flex rounded bg-slate-200 px-2 py-0.5 text-xs font-medium text-slate-700 dark:bg-slate-700 dark:text-slate-200">
            Today
          </span>
        </span>
      )}
    </>
  )
}

function ExpectedDay({ day }: { day: BroadcastPlanDay }) {
  const detail = expectedDayDetail(day)
  return (
    <>
      <span className="whitespace-nowrap text-[13px] text-slate-500 dark:text-slate-400">
        <span className="font-medium tabular-nums text-slate-700 dark:text-slate-200">{formatCount(day.expected)}</span> expected
      </span>
      {detail && <span className="text-xs text-slate-500 dark:text-slate-400">{detail}</span>}
    </>
  )
}

// "From 02:00 CEST, 100 an hour": when the first coming day opens, the event cutting a day short, and
// the hourly limit that paces it.
function expectedDayDetail(day: BroadcastPlanDay): string | null {
  const window: string[] = []
  if (day.startsAtUtc) window.push(`From ${localTimeWithZone(day.startsAtUtc)}`)
  if (day.endsAtUtc) window.push(`${window.length > 0 ? 'until' : 'Until'} ${localTimeWithZone(day.endsAtUtc)}`)
  const pace = day.ready ? 'no limit' : day.hourlyLimit ? `${formatCount(day.hourlyLimit)} an hour` : null
  if (window.length === 0) return pace && `${pace.charAt(0).toUpperCase()}${pace.slice(1)}`
  return pace ? `${window.join(' ')}, ${pace}` : window.join(' ')
}
